import {
  CashTransactionModel,
  CashTransactionMutationInput,
} from 'api-hooks/transactions/cash-transaction';
import { TransactionalStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { HalfContainer } from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { SectionContainer, Title, TitleContainer } from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { CashTransactionFormType, cashTransactionSchema } from './form-type';
import TransactionSection from './transaction-section';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  cashTransaction?: CashTransactionModel;
  onSubmit: (
    input: CashTransactionMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<CashTransactionModel | undefined>;
}

export default function CashTransactionForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { cashTransaction } = props;

  const defaultValues = useGetDefaultValues(cashTransaction);

  const resolver = useYupValidationResolver(cashTransactionSchema());

  const methods = useForm<CashTransactionFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        values.needApproval =
          values.transactionAt.getTime() !== values.tempTransactionAt.getTime();
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const isActive = cashTransaction?.status === TransactionalStatusEnum.Active;

  const permission = useGetCrudPermission('cash-transactions');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'cash-transactions',
    cashTransaction,
  );
  const { resolverButton } = useResolveApproval(
    'cash-transactions',
    cashTransaction,
  );
  const { requestApprovalButton } = useApprovalAction(
    'cash-transactions',
    cashTransaction,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'cash-transactions',
    cashTransaction,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (cashTransaction) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    cashTransaction,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!cashTransaction}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        cashTransaction &&
        `${t('common:cash_transaction')} - ${cashTransaction.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={cashTransaction}
            onDelete={onVoid}
            noDelete={editable || !voidPermission}
            noEdit={!isActive || !permission.update}
            title={
              cashTransaction ? (
                <TitleContainer $style={{ display: 'flex', gap: '16px' }}>
                  <Title variant="HeadingXSmall">{`${t(
                    'common:cash_transaction',
                  )} - ${cashTransaction.number}`}</Title>
                  <TransactionPillStatus value={cashTransaction.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', {
                  extra: t('common:cash_transaction'),
                })
              )
            }
            isDeleteLoading={voidMutate.isLoading}
            deleteText={t('common:void')}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          {cashTransaction && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:number')}
                placeholder={t('common:number')}
                disabled
                required
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              clearable={false}
              name="transactionAt"
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <TransactionSection />
      </div>
    </Form>
  );
}
